import React, { useState, useMemo } from 'react'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import CloseIcon from '@mui/icons-material/Close'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

// Custom Leaflet Icon using MUI HomeIcon SVG
const homeIcon = new L.DivIcon({
  html: `<div style="display: flex; align-items: center; justify-content: center; width: 32px; height: 32px; background: #1976D2; color: white; border-radius: 50%; box-shadow: 0 2px 6px rgba(0,0,0,0.3);">
            <svg xmlns="http://www.w3.org/2000/svg" height="20" viewBox="0 -960 960 960" width="20" fill="white">
              <path d="M480-64 120-424l60-60 60 60v-212L72-664l408-352 408 352-168 140v212l60-60 60 60-360 360Zm0-64 144-144v-248h-96v176H432v-176h-96v248l144 144Z"/>
            </svg>
         </div>`,
  className: '',
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
})

// This component renders the map.
const ListingLocationMap = ({ locationName, lat, lon, isExpanded, onExpandClick }) => {
  // Memoize the position so it only recalculates when lat or lon changes.
  const position = useMemo(() => [lat, lon], [lat, lon])

  // Use memoization to define container styles based on isExpanded.
  const containerStyle = useMemo(
    () => ({
      height: isExpanded ? '600px' : '400px',
      width: '100%',
      borderRadius: 8,
    }),
    [isExpanded]
  )

  return (
    <Card sx={{ position: 'relative', maxWidth: 600, mx: 'auto', boxShadow: 4, borderRadius: 3 }}>
      <CardContent sx={{ p: 0 }}>
        <MapContainer center={position} zoom={17} style={containerStyle}>
          <TileLayer
            attribution="&copy; OpenStreetMap contributors"
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Marker position={position} icon={homeIcon}>
            <Popup>
              <Typography variant="body2" sx={{ color: '#1976D2' }}>
                🏡 Home Location
              </Typography>
              <Typography variant="caption">
                {locationName}
                <br />
                Lat: {lat}, Lon: {lon}
              </Typography>
            </Popup>
          </Marker>
        </MapContainer>
        {/* Only show expand button when not in expanded mode */}
        {!isExpanded && (
          <IconButton
            onClick={onExpandClick}
            sx={{
              position: 'absolute',
              bottom: 16,
              right: 16,
              backgroundColor: 'white',
              boxShadow: 1,
              '&:hover': { backgroundColor: 'grey.200' },
            }}
          >
            <FullscreenIcon />
          </IconButton>
        )}
      </CardContent>
    </Card>
  )
}

// This component wraps the map and handles the modal for an expanded view.
const ExpandableMap = ({ locationName, lat, lon }) => {
  const [open, setOpen] = useState(false)
  const handleExpandClick = () => setOpen(true)
  const handleClose = () => setOpen(false)

  return (
    <div>
      {/* Original (non-expanded) map view with overlay expand button */}
      <ListingLocationMap
        locationName={locationName}
        lat={lat}
        lon={lon}
        isExpanded={false}
        onExpandClick={handleExpandClick}
      />

      {/* Modal displaying the expanded map view */}
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <IconButton
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1 }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ p: 0 }}>
          <ListingLocationMap locationName={locationName} lat={lat} lon={lon} isExpanded={true} />
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default ExpandableMap
